// LicenseProvider: owns the signed license token lifecycle in the browser.
//
//  - verifies tokens with the PUBLIC key map (kid → JWK) from
//    VITE_LICENSE_JWKS_JSON or GET {VITE_LICENSE_SERVER_URL}/license/jwks
//  - refreshes when <12 h remain (server POST /license/token, 24 h TTL)
//  - offline: token stays valid until `gu` (7-day grace), then the app
//    locks export
//  - never trusts client time as the source of truth
//
// This is deliberately NOT obfuscated: client-side checks can be bypassed;
// the token exists to give the app a clean, honest license signal.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  claimsFromPayload,
  fetchJwks,
  verifyLicenseToken,
  type JwkByKid,
} from '@imagelayerdecomposer/shared';
import {
  getOrCreateDeviceId,
  loadToken,
  loadLastSeenServerTime,
  saveLastSeenServerTime,
  saveToken,
} from './device';
import { isAuthConfigured, getSupabase } from '../lib/supabaseClient';
import { getStoredSessionToken, AUTH_CHANGED_EVENT } from '../auth/sessionToken';

export type LicensePhase = 'loading' | 'ready' | 'unconfigured' | 'locked';

export type LicenseState = {
  phase: LicensePhase;
  /** decoded claims of the current token (null when there is none) */
  claims: ReturnType<typeof claimsFromPayload>;
  /** epoch seconds when the entitlement runs out (exp, then grace gu) */
  locked: boolean;
  /** true when verified offline past exp but still inside grace */
  grace: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

const Ctx = createContext<LicenseState | null>(null);

const REFRESH_THRESHOLD_H = 12;
const POLL_MS = 5 * 60 * 1000;

let cachedJwks: JwkByKid | null = null;

async function loadJwks(): Promise<JwkByKid> {
  if (cachedJwks) return cachedJwks;
  const embedded = import.meta.env['VITE_LICENSE_JWKS_JSON'] as string | undefined;
  if (embedded) {
    try {
      cachedJwks = JSON.parse(embedded) as JwkByKid;
      return cachedJwks;
    } catch {
      /* fall through to endpoint */
    }
  }
  const serverUrl = (import.meta.env['VITE_LICENSE_SERVER_URL'] as string | undefined) ?? '';
  cachedJwks = await fetchJwks(`${serverUrl.replace(/\/$/, '')}/license/jwks`);
  return cachedJwks!;
}

async function requestToken(deviceId: string): Promise<string> {
  // Two bearer kinds, mirroring the server: a Supabase access token (when the
  // email flow is configured) or the app-wall session token. The app wall is
  // the only flow in production today, so it is the primary fallback here.
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (isAuthConfigured()) {
    const sb = getSupabase()!;
    const { data } = await sb.auth.getSession();
    const access = data.session?.access_token;
    if (!access) throw new Error('not signed in');
    headers.Authorization = `Bearer ${access}`;
  } else {
    const sessionToken = getStoredSessionToken();
    if (!sessionToken) throw new Error('not signed in');
    headers.Authorization = `Bearer ${sessionToken}`;
  }
  const serverUrl = ((import.meta.env['VITE_LICENSE_SERVER_URL'] as string | undefined) ?? '');
  const res = await fetch(`${serverUrl.replace(/\/$/, '')}/license/token`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ device_id: deviceId, label: navigator.userAgent.slice(0, 64) }),
  });
  if (res.status === 403) throw new Error('device limit reached. Revoke a device on the Account page.');
  if (!res.ok) throw new Error(`license token failed (HTTP ${res.status})`);
  const body = (await res.json()) as { token: string };
  return body.token;
}

export function LicenseProvider({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<LicensePhase>('loading');
  const [claims, setClaims] = useState<LicenseState['claims']>(null);
  const [grace, setGrace] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const deviceIdRef = useRef<string | null>(null);

  const verifyAndSet = useCallback(async (token: string | null, now = Math.floor(Date.now() / 1000)) => {
    const jwks = await loadJwks();
    if (!deviceIdRef.current) deviceIdRef.current = await getOrCreateDeviceId();
    const did = deviceIdRef.current;
    const lastSeen = await loadLastSeenServerTime();
    if (!token) {
      setPhase('ready'); // app usable but unlicensed → locked
      setClaims(null);
      return;
    }
    const r = await verifyLicenseToken(token, jwks, { deviceId: did, lastSeenServerTime: lastSeen, now });
    if (!r.ok) {
      setError(r.reason === 'unknown-kid' ? 'Unknown license key version' : r.reason);
      setClaims(null);
      setPhase(r.reason === 'clock-skew' ? 'locked' : 'ready');
      return;
    }
    setError(null);
    setGrace(r.claims.exp <= now);
    setClaims(r.claims);
    setPhase('ready');
    if (r.claims.iat > (lastSeen ?? 0)) await saveLastSeenServerTime(r.claims.iat);
  }, []);

  const refresh = useCallback(async () => {
    try {
      // App wall: nothing to fetch until a session exists (avoids noisy
      // failures on the login page). Supabase-configured builds always try.
      if (!isAuthConfigured() && !getStoredSessionToken()) return;
      if (!deviceIdRef.current) deviceIdRef.current = await getOrCreateDeviceId();
      const fresh = await requestToken(deviceIdRef.current);
      await saveToken(fresh);
      await verifyAndSet(fresh);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [verifyAndSet]);

  useEffect(() => {
    let disposed = false;
    (async () => {
      // dev fallback without auth → "trial" entitlements, no server calls
      if (import.meta.env.DEV && !isAuthConfigured()) {
        setPhase('unconfigured');
        setClaims({
          sub: 'dev-fallback-user',
          did: 'dev',
          plan: 'trial',
          status: 'trialing',
          entitlements: { export: true, watermark: false },
          iat: 0,
          exp: Math.floor(Date.now() / 1000) + 30 * 86400,
          gu: Math.floor(Date.now() / 1000) + 37 * 86400,
        });
        return;
      }
      try {
        const stored = await loadToken();
        await verifyAndSet(stored);
        if (stored) {
          // auto-refresh when the stored token is near expiry, or when a
          // trialing token predates the tee claim (banner needs the trial end)
          const jwks = await loadJwks();
          const t = Math.floor(Date.now() / 1000);
          const r = await verifyLicenseToken(stored, jwks, { deviceId: deviceIdRef.current! });
          const staleTe = r.ok && r.claims.status === 'trialing' && !r.claims.tee;
          if (r.ok && (r.claims.exp - t < REFRESH_THRESHOLD_H * 3600 || staleTe)) await refresh();
        } else if (getStoredSessionToken()) {
          // page load with an existing app-wall session but no license token
          // yet (or a full reload after login) → fetch one now
          await refresh();
        }
      } catch (err) {
        if (disposed) return;
        setError(err instanceof Error ? err.message : String(err));
        setPhase('ready');
      }
    })();
    const poll = setInterval(() => {
      if (claims && claims.exp - Math.floor(Date.now() / 1000) < REFRESH_THRESHOLD_H * 3600) void refresh();
    }, POLL_MS);
    // Login/logout from the app wall must re-embed licensing state.
    const onAuthChanged = () => {
      if (disposed) return;
      if (getStoredSessionToken()) void refresh();
      else void verifyAndSet(null); // logout → claims cleared (view-only)
    };
    window.addEventListener(AUTH_CHANGED_EVENT, onAuthChanged);
    return () => {
      disposed = true;
      clearInterval(poll);
      window.removeEventListener(AUTH_CHANGED_EVENT, onAuthChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  const value = useMemo<LicenseState>(
    () => ({
      phase,
      claims,
      grace,
      error,
      locked: !(claims && claims.entitlements.export && (grace || claims.exp > Math.floor(Date.now() / 1000))),
      refresh,
    }),
    [phase, claims, grace, error, refresh],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLicense(): LicenseState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useLicense outside LicenseProvider');
  return v;
}

/** Single point deciding whether exports are allowed (UI + runExport). */
export function canExport(state: LicenseState): boolean {
  return !state.locked;
}
